import { useCallback, useEffect, useState } from "react";

import {
  api,
  ApiError,
  type PlayerMoveToOwnAccountResult,
  type PlayerOwnAccessGrant,
  type PlayerOwnAccessStatus,
  type PlayerProfile,
} from "../lib/api";
import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import { familyProfilesPhrase } from "../lib/familyAccount";
import { accountNotOwnedNote } from "../lib/playerAccountOwnership";
import { maskEmailLikeServer } from "../lib/passwordRecovery";

/** Acceso propio para un perfil de cuenta familiar (Eduardo 2026-09-19).
 *
 * Reglas de Eduardo: lo otorga el admin y el tutor CONSERVA el acceso. Se abre
 * solo al pulsar "Acceso propio…" en un jugador (nada aparece sin una acción
 * previa). El jugador recibe su propio usuario y un PIN temporal; con ese
 * usuario solo ve SU perfil, no el de sus hermanos.
 *
 * «Pasar a su propia cuenta» (Eduardo 2026-10-04): para un ADULTO que alguien
 * inscribió con su propia cuenta (p. ej. la organizadora con sus jugadoras).
 * Con el acceso propio ya dado, su usuario queda como su cuenta principal y la
 * cuenta anterior deja de verlo. Siempre con confirmación que nombra a los dos. */

interface Props {
  token: string;
  // Solo se usan el id, el nombre y el género (para «la/lo»): lo abren la
  // lista de jugadores y la ficha del jugador.
  player: Pick<PlayerProfile, "id" | "display_name"> & { gender?: string | null };
  onClose: () => void;
}

interface MovedState {
  result: PlayerMoveToOwnAccountResult;
  /** Nombre de la cuenta anterior al confirmar (después el estado ya es otro). */
  holderName: string | null;
}

/** Usuario propio para mostrarlo: el correo enmascarado, o el usuario. */
function ownLoginLabel(info: { email: string | null; username: string | null } | null | undefined): string {
  if (info?.email) return maskEmailLikeServer(info.email);
  return info?.username ?? "su usuario";
}

export default function PlayerOwnAccessPanel({ token, player, onClose }: Props) {
  const { askConfirm } = useAbortableConfirm([player.id]);
  const [status, setStatus] = useState<PlayerOwnAccessStatus | null>(null);
  const [email, setEmail] = useState("");
  const [granted, setGranted] = useState<PlayerOwnAccessGrant | null>(null);
  const [moved, setMoved] = useState<MovedState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadStatus = useCallback(
    () => api.players.ownAccessStatus(token, player.id).then((response) => response.data),
    [token, player.id],
  );

  useEffect(() => {
    let cancelled = false;
    loadStatus()
      .then((data) => {
        if (cancelled) return;
        setStatus(data);
        setEmail(data.own_access?.email ?? "");
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "No se pudo consultar el acceso.");
      });
    return () => {
      cancelled = true;
    };
  }, [loadStatus]);

  async function refreshStatus() {
    try {
      setStatus(await loadStatus());
    } catch {
      // Se queda el estado anterior; la acción ya se hizo.
    }
  }

  async function handleGrant() {
    setBusy(true);
    setError(null);
    try {
      const response = await api.players.grantOwnAccess(token, player.id, { email: email.trim() || null });
      setGranted(response.data);
      setStatus((current) => (current ? { ...current, own_access: response.data.own_access } : current));
      // Con el acceso dado puede ofrecerse «Pasar a su propia cuenta».
      void refreshStatus();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo dar el acceso propio.");
    } finally {
      setBusy(false);
    }
  }

  async function handleRevoke() {
    const accepted = await askConfirm(
      `¿Quitar el acceso propio de "${player.display_name}"?\n\nSu usuario dejará de entrar. El perfil, sus torneos y el acceso del tutor no cambian.`,
      { acceptLabel: "Sí, quitar acceso propio", tone: "danger" },
    );
    if (!accepted) return;
    setBusy(true);
    setError(null);
    try {
      await api.players.revokeOwnAccess(token, player.id);
      setGranted(null);
      setStatus((current) => (current ? { ...current, own_access: null } : current));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo quitar el acceso propio.");
    } finally {
      setBusy(false);
    }
  }

  const ownAccess = status?.own_access ?? null;
  const alreadyLoggedIn = Boolean(ownAccess?.last_login_at);
  // A este jugador lo registró otra organización: el admin no puede darle ni
  // quitarle acceso propio ni pasarlo a su propia cuenta. Se explica sin
  // botones (Opción A, Eduardo 2026-09-30).
  const notOwned = status?.can_manage_account === false;
  const inOwnAccount = !notOwned && Boolean(status?.in_own_account);
  // Tiene acceso propio pero el perfil sigue en la cuenta de otra persona.
  const stillShared = Boolean(ownAccess) && !inOwnAccount;
  const familyView = !notOwned && !inOwnAccount && (Boolean(status?.is_family_profile) || stillShared);
  const move = status?.move_to_own_account ?? null;
  const pronoun = player.gender === "male" ? "lo" : "la";
  const holderName = status?.account_has_foreign_profiles ? null : status?.guardian?.full_name ?? null;

  async function handleMove() {
    const login = ownLoginLabel(ownAccess);
    const holder = holderName;
    const question = holder
      ? `${player.display_name} dejará la cuenta de ${holder} y quedará solo en su propia cuenta (${login}). ${holder} ya no ${pronoun} verá ni recibirá sus avisos.`
      : `${player.display_name} dejará la cuenta familiar con la que entra hoy y quedará solo en su propia cuenta (${login}). Esa cuenta ya no ${pronoun} verá ni recibirá sus avisos.`;
    const accepted = await askConfirm(
      `${question}\n\nSu perfil, sus torneos, pagos y resultados no cambian.`,
      { acceptLabel: "Sí, pasar a su propia cuenta" },
    );
    if (!accepted) return;
    setBusy(true);
    setError(null);
    try {
      const response = await api.players.moveToOwnAccount(token, player.id);
      setMoved({ result: response.data, holderName: holder });
      setStatus((current) =>
        current
          ? {
              ...current,
              in_own_account: true,
              is_family_profile: false,
              own_access: response.data.own_account ?? current.own_access,
              move_to_own_account: { available: false, code: null, reason: null },
            }
          : current,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo pasar a su propia cuenta.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="inline-confirm" role="dialog" aria-modal="true" aria-label="Acceso propio del jugador">
      <div className="inline-confirm__card player-merge">
        <h3 className="player-merge__title">Acceso propio</h3>
        <p>
          Jugador: <strong>{player.display_name}</strong>
        </p>
        {!status && !error ? <p className="muted">Consultando…</p> : null}

        {notOwned ? (
          <p className="muted" role="note" data-no-toast>
            {accountNotOwnedNote({
              hasOwner: status?.account_has_owner !== false,
              hasAccount: status?.has_account !== false,
            })}
          </p>
        ) : null}

        {inOwnAccount ? (
          moved ? (
            <div className="player-merge__roles" role="status" data-no-toast>
              <p>
                {moved.result.already ? "Ya estaba: " : "Listo: "}
                <strong>{player.display_name}</strong> está solo en su propia cuenta (
                {ownLoginLabel(moved.result.own_account ?? ownAccess)}).
                {moved.result.already
                  ? ""
                  : ` ${moved.holderName ?? "La cuenta anterior"} ya no ${pronoun} ve ni recibe sus avisos.`}
              </p>
              {moved.result.warnings.map((warning) => (
                <p key={warning} className="muted">
                  {warning}
                </p>
              ))}
            </div>
          ) : (
            <p className="muted" data-no-toast>
              {player.display_name} ya está en su propia cuenta: entra con{" "}
              <strong>{ownLoginLabel(ownAccess)}</strong> y ninguna otra cuenta ve su perfil.
            </p>
          )
        ) : null}

        {status && !notOwned && !familyView && !inOwnAccount ? (
          <p className="muted">
            Este perfil es el único de su cuenta: ya entra con su propio usuario. El acceso propio es para perfiles que
            cuelgan de la cuenta de un tutor junto con otros familiares.
          </p>
        ) : null}

        {familyView && status ? (
          <>
            {status.account_has_foreign_profiles ? (
              // La cuenta también la usan jugadores que no dio de alta la
              // organización de este admin: ni su nombre (suele ser el del
              // primer hijo) ni su correo.
              <p className="muted">
                Hoy entra con una cuenta familiar que también usan jugadores que no dio de alta tu organización. Con
                acceso propio recibe su usuario y solo ve su perfil; <strong>la cuenta familiar no cambia</strong> (lo
                demás de esa cuenta lo resuelve el soporte de Torneos123).
              </p>
            ) : (
              <p className="muted">
                Hoy entra con la cuenta familiar de <strong>{status.guardian?.full_name ?? "su tutor"}</strong>
                {status.family_profiles.length + (status.family_profiles_hidden ?? 0) > 0
                  ? `, junto con ${familyProfilesPhrase(status.family_profiles, status.family_profiles_hidden ?? 0)}`
                  : ""}
                . Con acceso
                propio recibe su usuario y solo ve su perfil; <strong>el tutor conserva el acceso</strong>.
              </p>
            )}

            {granted ? (
              <div className="player-merge__roles">
                <p>
                  Usuario: <strong>{granted.email ?? granted.username}</strong>
                </p>
                <p>
                  PIN temporal: <strong>{granted.pin}</strong>
                </p>
                <p className="muted">Cópialos ahora: el PIN no se vuelve a mostrar. Al entrar creará su contraseña.</p>
              </div>
            ) : ownAccess ? (
              <div className="player-merge__roles">
                <p>
                  Ya tiene acceso propio: <strong>{ownAccess.email ?? ownAccess.username}</strong>
                </p>
                <p className="muted">
                  {alreadyLoggedIn
                    ? "Ya entró con él. Si olvidó su contraseña, que use «Olvidé mi contraseña» o genérale un PIN con «Generar PIN»: va a su usuario, no al del tutor."
                    : "Todavía no entra. Puedes generarle un PIN nuevo."}
                </p>
              </div>
            ) : null}

            {stillShared ? (
              move?.available ? (
                <p className="muted">
                  Si es mayor de edad, con «Pasar a su propia cuenta» queda solo en su usuario y esa cuenta ya no{" "}
                  {pronoun} ve.
                </p>
              ) : move?.reason ? (
                <p className="muted">{move.reason}</p>
              ) : null
            ) : null}

            {!granted && !alreadyLoggedIn && status.is_family_profile ? (
              <label className="player-merge__search">
                <span>Correo del jugador (opcional)</span>
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="Distinto al del tutor; vacío = entra con su usuario"
                  disabled={busy}
                />
              </label>
            ) : null}
          </>
        ) : null}

        <div className="inline-confirm__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={busy}>
            {granted || moved ? "Cerrar" : "No, regresar"}
          </button>
          {familyView && ownAccess && !granted ? (
            <button type="button" className="btn btn--ghost btn--danger" onClick={() => void handleRevoke()} disabled={busy}>
              Quitar acceso propio
            </button>
          ) : null}
          {!notOwned && stillShared && move?.available ? (
            <button type="button" className="btn btn--ghost" onClick={() => void handleMove()} disabled={busy}>
              Pasar a su propia cuenta
            </button>
          ) : null}
          {familyView && status?.is_family_profile && !granted && !alreadyLoggedIn ? (
            <button type="button" className="btn" onClick={() => void handleGrant()} disabled={busy}>
              {busy ? "Generando…" : ownAccess ? "Generar PIN nuevo" : "Dar acceso propio"}
            </button>
          ) : null}
        </div>
        {error ? <p className="player-merge__blocker">{error}</p> : null}
      </div>
    </div>
  );
}
